import { useEffect, useState } from 'react';
import {
  SalesOrderWeeklyPhaseSummary,
  SalesOrderWeeklySummary,
  SalesOrderWeeklySummaryComparison,
} from '@/hooks/api/models';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { MotionDiv } from '@/components/element/MotionDiv';
import { useNavigate } from 'react-router-dom';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useDataTableSortContent } from '@/hooks/useDataTableSortContent';

type Props = {
  phase: string;
  phaseLabel?: string;
  beforeAmount?: number | null;
  afterAmount?: number | null;
  weeklySummaries?: SalesOrderWeeklySummary[] | null;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
};

export const ComparisonPhaseWeeklySummaries = ({
  phase,
  phaseLabel,
  beforeAmount,
  afterAmount,
  weeklySummaries,
  collapsed,
  setCollapsed,
}: Props) => {
  const navigate = useNavigate();
  const { _w_ } = useCurrentWorkspace();
  // const [collapsed, setCollapsed] = useState(false);
  //
  // useEffect(() => {
  //   if (weeklySummaries) setCollapsed(false);
  // }, [weeklySummaries]);

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const assignTemplate = (s: SalesOrderWeeklySummary) => (
    <div className="d-flex align-items-center">
      <img
        src={s.user.user_account.icon_file_url}
        className="avatar avatar-xs me-2 rounded-circle"
        alt="avatar image"
      />
      <h6 className="mb-0 text-xxs">{s.user.user_account.name}</h6>
    </div>
  );

  const actualSalesTemplate = (s: SalesOrderWeeklySummary) => {
    if (s.state === 'out') {
      return '-';
    } else {
      return `¥${s.accounting_total_amount.toLocaleString()}`;
    }
  };

  const diffTemplate = (s: SalesOrderWeeklySummary) => `¥${s.diff.toLocaleString()}`;

  // DataTableのソート、キーワード検索
  const { dataTableSortContent, onSort, onKeywordChanged } = useDataTableSortContent(
    `weekly_comparison_${_w_!.id}_${phase}`
  );

  // 検索ワードの設定
  useEffect(() => {
    let _filters = { ...filters };
    _filters.global.value = dataTableSortContent.dataTableSearchKeyword?.keyword || '';
    setFilters(_filters);
  }, [dataTableSortContent]);

  if (!weeklySummaries) {
    return null;
  }

  return (
    <MotionDiv className="col-12">
      <div className="card mb-4">
        <div className="card-header accordion-header pb-0 pt-3 d-flex align-items-center justify-content-between">
          <h6 className="mb-0">
            「{phaseLabel}」Details
            <span className="ms-2 text-xs">
              {beforeAmount != null && afterAmount != null ? (
                <>
                  ¥{beforeAmount.toLocaleString()} → ¥{afterAmount.toLocaleString()}
                </>
              ) : (
                <></>
              )}
            </span>
          </h6>
          {weeklySummaries.length > 0 && (
            <button
              className="btn btn-icon-only"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#collapse_${phase}`}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? (
                <i className="collapse-open fa fa-minus text-xs pt-1"></i>
              ) : (
                <i className="collapse-close fa fa-plus text-xs pt-1"></i>
              )}
            </button>
          )}
        </div>
        <div className={`collapse ${collapsed ? 'show' : ''}`} id={`collapse_${phase}`}>
          <div className="card-header mt-3 pt-0 pb-0 d-flex justify-content-between">
            <div></div>
            <div>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  className="form-control"
                  placeholder="Search"
                  type="text"
                  value={filters.global.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    let _filters = { ...filters };
                    _filters.global.value = value;
                    setFilters(_filters);
                    onKeywordChanged(value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="card-body pb-0">
            <div className="table-responsive">
              <DataTable
                value={weeklySummaries}
                stripedRows
                scrollable
                scrollHeight="300px"
                tableClassName="table data-table align-items-center clickable-row"
                filters={filters}
                globalFilterFields={[
                  'state',
                  'business_partner.display_name',
                  'name',
                  'team_outline.name',
                  'user.user_account.name',
                  'accounting_total_amount',
                  'diff',
                ]}
                emptyMessage="No Data."
                removableSort
                onRowClick={(e) => {
                  navigate(`/w/${_w_!.identifier}/teams/${e.data.team_outline.id}/sales_orders/${e.data.id}`);
                }}
                onSort={onSort}
                sortField={dataTableSortContent.dataTableSortField?.filed}
                sortOrder={dataTableSortContent.dataTableSortField?.sortOrder}
              >
                <Column
                  header="ステータス"
                  field="state"
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  frozen
                  sortable
                ></Column>
                <Column
                  header="取引先"
                  field="business_partner.display_name"
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  headerStyle={{ backgroundColor: '#f9fafb' }}
                  bodyStyle={{
                    backgroundColor: 'inherit',
                    maxWidth: '150px',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                  frozen
                  sortable
                ></Column>
                <Column
                  header="プロジェクト名"
                  field="name"
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  headerStyle={{ backgroundColor: '#f9fafb' }}
                  bodyStyle={{
                    backgroundColor: 'inherit',
                    maxWidth: '150px',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                  frozen
                  sortable
                ></Column>
                <Column
                  header="チーム"
                  field="team_outline.name"
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  sortable
                ></Column>
                <Column
                  header="担当者"
                  field="user.user_account.name"
                  body={assignTemplate}
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  sortable
                ></Column>
                <Column
                  header="売上金額"
                  field="accounting_total_amount"
                  body={actualSalesTemplate}
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  sortable
                ></Column>
                <Column
                  header="差分"
                  field="diff"
                  body={diffTemplate}
                  bodyClassName="text-xxs px-2 font-weight-bold"
                  headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                  sortable
                ></Column>
              </DataTable>
            </div>
          </div>
        </div>
        <div className="card-footer pt-0 pb-3"></div>
      </div>
    </MotionDiv>
  );
};
